import { ModuleConfig } from '@intellio/shared/models';

export const FILE_UPLOAD_SUPPORTED_EXTENSIONS_CONFIG_ID =
  'IntelligentActionSupportedFileExtensions';
export const FILE_UPLOAD_FILE_TYPES_CONFIG_ID = 'IntelligentActionFileTypeIds';
export const FILE_UPLOAD_APP_TYPES_CONFIG_ID =
  'IntelligentActionApplicationTypeIds';
export const FILE_UPLOAD_ENERGY_SOURCES_CONFIG_ID =
  'IntelligentActionApplicationEnergySources';

export interface FileUploadValidationConfig {
  fileExtensions: string[];
  intellioFileTypes: string[];
  appTypes: string[];
  energySources: string[];
}

export interface FileUploadValidationContext {
  appTypeId?: string;
  energySourceId?: string;
  fileTypeId?: string;
  fileName?: string;
}

export const emptyFileUploadValidationConfig =
  (): FileUploadValidationConfig => ({
    fileExtensions: [],
    intellioFileTypes: [],
    appTypes: [],
    energySources: [],
  });

export function parseFileUploadValidationConfig(
  fileManagementModuleConfigs?: ModuleConfig[]
): FileUploadValidationConfig {
  const configs = fileManagementModuleConfigs ?? [];

  return {
    fileExtensions: parseDelimitedConfig(
      configs.find(
        (config) =>
          config.configId === FILE_UPLOAD_SUPPORTED_EXTENSIONS_CONFIG_ID
      )?.value,
      true
    ),
    intellioFileTypes: parseDelimitedConfig(
      configs.find(
        (config) => config.configId === FILE_UPLOAD_FILE_TYPES_CONFIG_ID
      )?.value
    ),
    appTypes: parseDelimitedConfig(
      configs.find(
        (config) => config.configId === FILE_UPLOAD_APP_TYPES_CONFIG_ID
      )?.value
    ),
    energySources: parseDelimitedConfig(
      configs.find(
        (config) => config.configId === FILE_UPLOAD_ENERGY_SOURCES_CONFIG_ID
      )?.value
    ),
  };
}

export function isUnsupportedFileUpload(
  config: FileUploadValidationConfig,
  context: FileUploadValidationContext
): boolean {
  return (
    shouldValidateFileUpload(config, context) &&
    !isSupportedFileUploadExtension(config, context.fileName)
  );
}

export function shouldValidateFileUpload(
  config: FileUploadValidationConfig,
  context: FileUploadValidationContext
): boolean {
  if (!hasCompleteUploadValidationConfig(config)) {
    return false;
  }

  return (
    config.intellioFileTypes.includes(context.fileTypeId?.toLowerCase()) &&
    config.appTypes.includes(context.appTypeId?.toLowerCase()) &&
    config.energySources.includes(context.energySourceId?.toLowerCase())
  );
}

export function isSupportedFileUploadExtension(
  config: FileUploadValidationConfig,
  fileName?: string
): boolean {
  return config.fileExtensions.includes(getFileExtension(fileName));
}

export function formatSupportedFileUploadExtensions(
  config: FileUploadValidationConfig,
  useConjunction = false
): string {
  const extensions = config.fileExtensions.map((extension) =>
    extension.replace(/^\./, '').toUpperCase()
  );

  if (!useConjunction || extensions.length < 2) {
    return extensions.join(', ');
  }

  return `${extensions.slice(0, -1).join(', ')}, or ${
    extensions[extensions.length - 1]
  }`;
}

function parseDelimitedConfig(
  value?: string,
  normalizeExtension = false
): string[] {
  return (value ?? '')
    .split(',')
    .map((entry) => entry?.trim().toLowerCase())
    .filter((entry) => entry)
    .map((entry) =>
      normalizeExtension && !entry.startsWith('.') ? `.${entry}` : entry
    );
}

function hasCompleteUploadValidationConfig(
  config: FileUploadValidationConfig
): boolean {
  return (
    config.fileExtensions.length > 0 &&
    config.intellioFileTypes.length > 0 &&
    config.appTypes.length > 0 &&
    config.energySources.length > 0
  );
}

function getFileExtension(fileName?: string): string {
  const extensionStart = fileName?.lastIndexOf('.') ?? -1;
  return extensionStart > -1
    ? fileName.substring(extensionStart).toLowerCase()
    : '';
}
